import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Handle, Position, useStore } from 'reactflow'
import type { NodeStatus } from '../../../../generated/nodeDataTypes'
import { cn } from '../../../../lib/utils'
import { NODE_CARD_RADIUS_CLASS } from './constants'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { FloatingNodeLabel } from './FloatingNodeLabel'
import { useCanvasAssetLoad } from '../../../../analytics/useCanvasAssetLoad'
import { outputVariantId } from '../../../../analytics/outputVariantId'
import type { AssetKind } from '../../../../analytics/events'
import type { HandleConfig } from './types'
import { NodeStatusMessage } from './NodeStatusMessage'
import { createNadDebug } from '../../../../utils/debugLog'
import { useOptionalProjectId } from '../../../../contexts/ProjectIdContext'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from './MetaLoadingSurface'

const errorBannerLog = createNadDebug('dw:node-error-banner:visibility')

type FlatIdleNodeMediaKind = 'image' | 'video' | 'audio' | 'none'

export interface FlatIdleNodeCardProps {
  /** Owning node id — gates handle rendering by per-node active-only visibility. */
  nodeId: string
  label?: string
  fallbackLabel: string
  typeIcon?: ReactNode
  width: number
  status: NodeStatus
  handles: HandleConfig[]
  mediaKind: FlatIdleNodeMediaKind
  mediaUrl?: string | null
  /** First-frame poster URL for video media (RFC-0931); renders a cheap <img>
   * at idle. Falls back to <video> if absent or the poster fails to load. */
  posterUrl?: string | null
  aspectRatio?: string
  aspectClassName?: string
  /**
   * How image media fills the reserved box. Defaults to `'cover'` (crop to
   * fill), which is what every caller wants when the media's aspect matches
   * the box. Designer nodes pass `'contain'` because their render aspect is
   * provider-dependent and may not match the fixed 16:9 box — see the
   * `object-contain` note in `DesignerNodeShell` (T287457882). Applies to the
   * image branch only; video keeps `cover`.
   */
  mediaFit?: 'cover' | 'contain'
  error?: string
  progressMessage?: string
  /** Explicit opt-in. Excluded and future callers keep the existing spinner. */
  metaLoading?: MetaLoadingDescriptor
  placeholderLabel: string
  testId: string
  compactHandles?: boolean
  backgroundColor?: string
  footerReserveHeight?: number
  footer?: ReactNode
  /**
   * Set ONLY when the media is the node's GENERATED output (not an upload):
   * the picked history index, sent as `variant_id` on
   * `canvas_asset_load_finished` (RFC-1577). Null/undefined when unknown.
   */
  outputVariantIndex?: number | null
}

function flatHandleClassName(handle: HandleConfig, compactHandles: boolean | undefined): string {
  if (compactHandles) return '!bg-transparent !border-0 !w-2 !h-2'
  return handle.position === Position.Top || handle.position === Position.Bottom
    ? '!bg-transparent !border-0 !w-4 !h-1'
    : '!bg-transparent !border-0 !w-1 !h-4'
}

function parseAspectRatio(value: string | undefined): number | null {
  if (!value) return null
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/)
  if (!match) return null
  const width = Number(match[1])
  const height = Number(match[2])
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null
  return width / height
}

const FlatIdleNodeCard = memo(function FlatIdleNodeCard(props: FlatIdleNodeCardProps) {
  const { t } = useTranslation('canvas')
  const nodeType = useStore((s) => s.nodeInternals.get(props.nodeId)?.type)
  const {
    label,
    fallbackLabel,
    typeIcon,
    width,
    status,
    handles,
    mediaKind,
    mediaUrl,
    posterUrl,
    aspectRatio,
    aspectClassName,
    mediaFit = 'cover',
    error,
    progressMessage,
    metaLoading,
    placeholderLabel,
    testId,
    compactHandles,
    backgroundColor,
    footerReserveHeight,
    footer,
    outputVariantIndex,
    nodeId,
  } = props
  const handlesVisible = useHandlesVisibleForNode(nodeId)
  // Nodes inside a group stay anonymous here. Zoomed out, a nine-panel video
  // group would otherwise stack nine "Scene N" labels into roughly one card's
  // worth of space and bury the group's own name — which is the name you
  // actually need at that distance. The group keeps its label, and so do
  // standalone nodes (characters, locations, script writer). Zoom back in past
  // the threshold and the full card names each panel again.
  //
  // Boolean selector, so a node re-renders only if it is moved in or out of a
  // group — not on unrelated store changes.
  const isInsideGroup = useStore((state) => Boolean(state.nodeInternals.get(nodeId)?.parentId))
  // Poster load failure (404 for a not-yet-backfilled video, or an extraction
  // gap) → fall back to the real <video>. Reset when the poster URL changes.
  const [posterFailed, setPosterFailed] = useState(false)
  useEffect(() => {
    setPosterFailed(false)
  }, [posterUrl])
  const isBusyStatus =
    status === 'running' ||
    status === 'pending_execution' ||
    status === 'pending_cost_confirmation'
  const displayError = error?.trim()
  const projectId = useOptionalProjectId()
  const lastLoggedError = useRef<string>()
  useEffect(() => {
    if (!displayError) {
      lastLoggedError.current = undefined
      return
    }
    if (lastLoggedError.current === displayError) return
    lastLoggedError.current = displayError
    errorBannerLog({
      event: 'error-banner-visible',
      presentation: 'lightweight',
      project_id: projectId,
      node_id: nodeId,
      status,
      card: testId,
      message: displayError,
    })
  }, [displayError, nodeId, projectId, status, testId])

  // App-health asset-load timing (RFC-0974 §5.5). The PRIMARY on-canvas media is
  // this card's rendered image/poster/video — the effective src drives one
  // attempt each: a poster→video fallback is two distinct source attempts (one
  // terminal each). Audio/none/running ⇒ no attempt (src empty).
  //
  // `asset_kind` reflects the ELEMENT actually being timed, not the node's media
  // kind: a video node's first-frame poster is an <img>, so it is measured as
  // `image` — otherwise "Video load P75" would really be measuring poster-image
  // decode. Only the real <video> fallback is measured as `video`.
  const showingPoster = mediaKind === 'video' && !!posterUrl && !posterFailed
  const assetKind: AssetKind = mediaKind === 'video' && !showingPoster ? 'video' : 'image'
  const assetSrc = isBusyStatus
    ? null
    : mediaKind === 'image'
      ? mediaUrl
      : mediaKind === 'video'
        ? showingPoster
          ? posterUrl
          : mediaUrl
        : null
  const asset = useCanvasAssetLoad({
    assetKind,
    src: assetSrc,
    nodeId,
    mediaType: mediaKind === 'video' ? 'video' : 'image',
    variantId: outputVariantId(nodeId, outputVariantIndex),
  })
  const ratio = parseAspectRatio(aspectRatio)
  const mediaHeight = ratio ? width / ratio : null
  const reservedFooterHeight = footerReserveHeight && footerReserveHeight > 0 ? footerReserveHeight : 0
  const fixedCardHeight = mediaHeight != null ? mediaHeight + reservedFooterHeight : null
  const mediaStyle = mediaHeight != null
    ? { height: mediaHeight }
    : aspectRatio
      ? { aspectRatio }
      : undefined

  return (
    // The label has to be a SIBLING of the card, not a child. It sits above the
    // card's top edge, and the card is both `overflow-hidden` and (via
    // `canvas-node-card-contained`) `contain: paint` — either one on its own
    // would clip the label away entirely. This mirrors how the full card is
    // built, where the label is likewise a sibling at the node root.
    <div className="relative w-fit">
      {/* Same label component the full card uses, so nothing shifts or
        * restyles as the canvas crosses the lightweight-render zoom threshold.
        * Read-only here: no `onRename`, because this card only exists while the
        * node is idle and unhovered — hover or select it and the full card
        * comes back with its editable label. The label is a fixed size on screen
        * at every zoom — see CONSTANT_SCREEN_SIZE_STYLE — which is what keeps it
        * readable here, where the name is the only way left to tell one node
        * from another. */}
      {!isInsideGroup && (
        <FloatingNodeLabel
          name={label ?? ''}
          fallback={fallbackLabel}
          typeIcon={typeIcon}
        />
      )}

      <div
        data-testid={testId}
        data-flat-idle-node="true"
        data-flat-idle-node-preview="true"
        data-nux-anchor="node-visible"
        data-nux-node-id={nodeId}
        data-nux-node-type={nodeType}
        data-nux-node-status={status}
        className={cn(
          'canvas-node-card-contained relative overflow-hidden border bg-surface-0 shadow-sm',
          NODE_CARD_RADIUS_CLASS,
          aspectClassName,
          displayError ? 'border-destructive/60' : isBusyStatus ? 'border-warning/60' : 'border-border',
        )}
        style={{
          width,
          ...(fixedCardHeight != null
            ? { height: fixedCardHeight }
            : aspectRatio
              ? { aspectRatio }
              : {}),
          backgroundColor,
        }}
        title={displayError || (isBusyStatus ? metaLoading?.message || progressMessage : undefined) || placeholderLabel}
      >
        {handlesVisible && handles.map((handle) => (
          <Handle
            key={`${handle.type}-${handle.id}`}
            type={handle.type}
            position={handle.position}
            id={handle.id}
            className={flatHandleClassName(handle, compactHandles)}
            style={handle.style}
          />
        ))}

        {isBusyStatus && metaLoading ? (
          <MetaLoadingSurface
            {...metaLoading}
            projectId={projectId}
            nodeId={nodeId}
            aspectRatio={aspectRatio}
            presentation="lightweight"
            style={reservedFooterHeight > 0 ? { bottom: reservedFooterHeight } : undefined}
          />
        ) : isBusyStatus ? (
          <div
            aria-label={progressMessage || t('flatIdleNodeCard.working')}
            className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-warning border-t-transparent animate-spin"
          />
        ) : mediaKind === 'image' && mediaUrl ? (
          <img
            ref={asset.mediaRef}
            src={mediaUrl}
            alt=""
            decoding="async"
            loading="lazy"
            className={`block h-full w-full ${mediaFit === 'contain' ? 'object-contain' : 'object-cover'}`}
            style={mediaStyle}
            onLoad={asset.onReady}
            onError={asset.onError}
          />
        ) : mediaKind === 'video' && mediaUrl ? (
          posterUrl && !posterFailed ? (
            // Idle: cheap first-frame <img> poster (RFC-0931) instead of a heavy
            // <video>. Same reserved box (mediaStyle) → no reflow on hover swap.
            <img
              ref={asset.mediaRef}
              src={posterUrl}
              alt=""
              decoding="async"
              loading="lazy"
              className="block h-full w-full object-cover"
              style={mediaStyle}
              onLoad={asset.onReady}
              onError={() => {
                // Compose: record the failed poster attempt AND fall back to <video>
                // (a new src ⇒ a new asset-load attempt for the video).
                asset.onError()
                setPosterFailed(true)
              }}
            />
          ) : (
            // Fallback (no poster yet / poster 404): today's <video> behavior.
            <video
              ref={asset.mediaRef}
              src={mediaUrl}
              muted
              playsInline
              preload="metadata"
              className="block h-full w-full object-cover"
              style={mediaStyle}
              onLoadedData={asset.onReady}
              onError={asset.onError}
            />
          )
        ) : mediaKind === 'audio' ? (
          <div
            aria-label={placeholderLabel}
            className="absolute inset-0 flex items-center px-4 text-foreground"
          >
            <svg
              aria-hidden="true"
              className="h-12 w-full"
              viewBox="0 0 160 48"
              fill="currentColor"
              preserveAspectRatio="none"
            >
              <path
                className="opacity-25"
                d="M0 22h6l3-8 4 20 5-28 5 36 5-25 5 14 5-19 5 25 5-33 5 39 5-24 5 10 5-16 5 22 5-29 5 36 5-23 5 12 5-17 5 24 5-31 5 37 5-22 5 9 5-14 5 20 5-26 5 31 5-18 5 7h5v4h-5l-5 7-5-18-5 31-5-26-5 20-5-14-5 9-5-22-5 37-5-31-5 24-5-17-5 12-5-23-5 36-5-29-5 22-5-16-5 10-5-24-5 39-5-33-5 25-5-19-5 14-5-25-5 36-5-28-4 20-3-8H0z"
              />
            </svg>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-xxs text-foreground/25">
            {placeholderLabel}
          </div>
        )}

        {displayError && (
          // Keep the existing node banner visible at idle/low zoom. Overlay the
          // media above any reserved footer without changing the card's box.
          <div className="pointer-events-none absolute inset-x-0" style={{ bottom: reservedFooterHeight }}>
            <NodeStatusMessage message={displayError} testId={`${testId}-error`} />
          </div>
        )}

        {footer ? (
          <div className="absolute inset-x-0 bottom-0">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
})

FlatIdleNodeCard.displayName = 'FlatIdleNodeCard'

export { FlatIdleNodeCard }
